<template>
  <div class="w-full" data-testid="pinning-session-entry">
    <!-- Completed session summary -->
    <div
      v-if="artwork.pinningSessionId"
      class="flex flex-col gap-3 rounded-lg border-2 border-green-400 bg-green-100 p-4"
      data-testid="completed-session-indicator"
    >
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold text-gray-900 sm:text-base">Your Artist Edits</span>
          <div v-click-outside="() => (showInfoTooltip = false)" class="group relative inline-flex">
            <button
              type="button"
              class="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
              aria-label="Design services information"
              data-testid="design-services-info-tooltip"
              @click="showInfoTooltip = !showInfoTooltip"
            >
              <UseSvg icon="info-circle-thin" class="h-4 w-4 text-gray-500 hover:text-gray-700" />
            </button>
            <div
              :class="[
                'absolute bottom-full right-0 z-50 mb-2 w-max max-w-[250px] rounded-md bg-blue-700 p-3 text-sm text-white shadow-lg transition-opacity',
                showInfoTooltip
                  ? 'pointer-events-auto opacity-100'
                  : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100'
              ]"
            >
              <p class="m-0 text-wrap">
                Our design team will perform the requested changes once the order is completed
              </p>
              <div
                class="absolute right-1.5 top-full border-[6px] border-transparent border-t-blue-700"
              />
            </div>
          </div>
        </div>
        <div
          v-if="artwork.pinningSummary"
          class="text-sm text-gray-700"
          data-testid="markdown-content"
        >
          <div
            class="markdown-rendered text-inherit"
            v-html="renderMarkdown(artwork.pinningSummary)"
          />
        </div>
      </div>
      <div class="flex gap-2 self-end">
        <button
          type="button"
          class="shrink-0 cursor-pointer rounded-[8px] border border-[#c90505] bg-transparent px-3.5 py-2 text-sm font-semibold [font-family:inherit] hover:bg-[rgba(201,5,5,0.06)] disabled:pointer-events-none disabled:opacity-25"
          style="color: #c90505"
          data-testid="cancel-design-request-button"
          @click="handleRemove"
        >
          Cancel
        </button>
        <button
          type="button"
          class="shrink-0 cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-2 text-sm font-semibold !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
          data-testid="edit-design-request-button"
          @click="handleEdit"
        >
          Add More Edits
        </button>
      </div>
    </div>

    <!-- New session input -->
    <div
      v-else
      class="border! overflow-hidden rounded-lg border-[#b3d4ff] focus-within:border-[#414192]"
      :class="{ 'cursor-not-allowed opacity-60': isEnhancementInProgress }"
    >
      <div class="bg-[#e4efff] px-2 py-1.5 md:px-3 md:py-2">
        <span class="text-center text-sm font-semibold text-[#414192]">Get Free Artist Edits</span>
      </div>
      <div class="flex flex-col gap-2 px-2 py-1.5 md:px-3 md:py-2">
        <textarea
          v-model="inputMessage"
          data-testid="request-designer-edit-input"
          class="mp-unmask border-0! outline-none! min-h-0 resize-none p-0 text-base placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60 md:text-sm"
          placeholder="Type your edit and we'll guide you through it"
          rows="2"
          :disabled="isEnhancementInProgress"
          @input="debouncedTrackEntryInput"
          @keydown="handleKeyDown"
        />
        <div class="flex justify-end">
          <button
            type="button"
            data-testid="request-designer-edit-submit"
            :class="[
              'cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-1.5 text-sm font-semibold !text-white [font-family:inherit] hover:bg-[#06065a] disabled:pointer-events-none disabled:opacity-25',
              !inputMessage.trim() && !isLoading && 'invisible'
            ]"
            :disabled="isLoading || isEnhancementInProgress"
            @click="handleSubmitMessage"
          >
            {{ isLoading ? "Submitting\u2026" : "Submit" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import UseSvg from "common/vue/components/UseSvg.vue";
import debounce from "lodash.debounce";
import { computed, ref } from "vue";
import { useDtfStore } from "../store";
import { useMarkdownRenderer } from "./composables/useMarkdownRenderer";
import { trackTransfersPinningSessionEntryInput } from "./transfersPinningAnalytics";

defineOptions({ name: "PinningSessionEntry" });

const { renderMarkdown } = useMarkdownRenderer();

const props = defineProps({
  index: { type: Number, required: true },
  artwork: { type: Object, required: true }
});

const store = useDtfStore();
const isLoading = computed(() => store.pinningSessionLoading);
const isEnhancementInProgress = computed(() => {
  if (!props.artwork?.id || props.artwork.isSimulated) return true;
  if (props.artwork.isLoading) return true;
  return store.isStandardArtwork && !props.artwork.enhancementCompleted;
});
const showInfoTooltip = ref(false);
const inputMessage = ref("");

const debouncedTrackEntryInput = debounce(() => {
  if (props.artwork?.id) {
    trackTransfersPinningSessionEntryInput({
      artworkId: props.artwork.id,
      inputValue: inputMessage.value
    });
  }
}, 500);

async function handleSubmitMessage() {
  const trimmed = inputMessage.value.trim();
  if (!trimmed || isLoading.value || isEnhancementInProgress.value) return;
  await store.openPinningSession(props.index, trimmed);
  if (store.showPinningModal) {
    inputMessage.value = "";
  }
}

function handleKeyDown(e) {
  if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
    e.preventDefault();
    handleSubmitMessage();
  }
}

function handleEdit() {
  store.editPinningSession(props.index);
}

function handleRemove() {
  store.clearPinningSession(props.index);
}
</script>

<style scoped>
.markdown-rendered :deep(ul) {
  margin-left: 1rem;
  list-style-type: disc;
}

.markdown-rendered :deep(ul li) {
  list-style-type: disc;
}

.markdown-rendered :deep(ul),
.markdown-rendered :deep(ol) {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.markdown-rendered :deep(ol) {
  margin-left: 1rem;
  list-style-type: decimal;
}

.markdown-rendered :deep(ol li) {
  list-style-type: decimal;
}

.markdown-rendered :deep(p) {
  margin-bottom: 0.5rem;
}

.markdown-rendered :deep(p:last-child) {
  margin-bottom: 0;
}

.markdown-rendered :deep(strong) {
  font-weight: 600;
}

.markdown-rendered :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}

.markdown-rendered :deep(a:hover) {
  color: #1e40af;
}

.markdown-rendered :deep(blockquote) {
  border-left: 4px solid #d1d5db;
  padding-left: 0.75rem;
  font-style: italic;
  color: #4b5563;
}

.markdown-rendered :deep(code) {
  border-radius: 0.25rem;
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  font-family: ui-monospace, monospace;
  font-size: 0.875rem;
}

.markdown-rendered :deep(pre) {
  overflow-x: auto;
  border-radius: 0.375rem;
  background-color: #f3f4f6;
  padding: 0.5rem;
  font-size: 0.875rem;
}
</style>
